import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { Children, forwardRef } from 'react';
import classNames from 'classnames';

const renderItems = (children, childClassName, firstChildClassName, lastChildClassName) => {
  return Children.map(children, (child, i) => {
    if (child == null) {
      return null;
    }

    let computedChildClassName;

    if (i === 0 && firstChildClassName != null) {
      computedChildClassName = firstChildClassName;
    } else if (!Object.prototype.hasOwnProperty.call(children, i + 1) && lastChildClassName != null) {
      computedChildClassName = lastChildClassName;
    } else {
      computedChildClassName = childClassName;
    }

    const className = classNames("uiListItem private-list__item", computedChildClassName);
    return /*#__PURE__*/_jsx("li", {
      className: className,
      children: child
    });
  });
};

const UIList = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    ordered,
    styled,
    inline,
    className,
    children,
    childClassName,
    firstChildClassName,
    lastChildClassName,
    use
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["ordered", "styled", "inline", "className", "children", "childClassName", "firstChildClassName", "lastChildClassName", "use"]);

  const computedClassName = classNames('uiList', (inline || use === 'inline-divided') && 'private-list--inline', className, !styled && 'private-list--unstyled', use === 'inline-divided' && 'private-list--inline-divided');
  const renderedItems = renderItems(children, childClassName, firstChildClassName, lastChildClassName);
  const List = ordered ? 'ol' : 'ul';
  return /*#__PURE__*/_jsx(List, Object.assign({}, rest, {
    className: computedClassName,
    ref: ref,
    children: renderedItems
  }));
});
UIList.propTypes = {
  /**
   * Sets the class name on each child in the list
   */
  childClassName: PropTypes.string,

  /**
   * Sets the content that will render as items in a list
   */
  children: PropTypes.node,

  /**
   * Sets the class name on the first child in the list
   */
  firstChildClassName: PropTypes.string,

  /**
   * Determines if the children should render horizontally instead of vertically
   */
  inline: PropTypes.bool.isRequired,

  /**
   * Sets the class name on the final child in the list
   */
  lastChildClassName: PropTypes.string,

  /**
   * Numbers each item in the list markup. To render visual numbers as well, set the `styled` prop to true
   */
  ordered: PropTypes.bool.isRequired,

  /**
   * Adds a bullet point to the beginning of each item in the list
   */
  styled: PropTypes.bool.isRequired,

  /**
   * Determines if list should be rendered inline and with dividers
   */
  use: PropTypes.oneOf(['default', 'inline-divided'])
};
UIList.defaultProps = {
  inline: false,
  ordered: false,
  styled: false,
  childClassName: '',
  use: 'default'
};
UIList.displayName = 'UIList';
export default UIList;